/**
 * ProtectedRoute — si loading se alarga, no se queda eterno: va a login.
 * Soporta ?next= para volver al Workspace tras autenticarse.
 */
import { useEffect, useState } from 'react'
import { LOGO_PROCREDITO } from '@/assets/logo'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/stores/auth.store'
import { ForcePasswordChangeScreen } from './ForcePasswordChangeScreen'

const LOADING_MAX_MS = 4000

function LoadingScreen({ stuck }: { stuck?: boolean }) {
  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{ background: 'linear-gradient(135deg, #050d1f 0%, #0a1628 50%, #0d1f3c 100%)' }}
    >
      <div className="flex flex-col items-center gap-4 px-6 text-center">
        <img
          src={LOGO_PROCREDITO}
          alt="ProCredito"
          className="h-16 w-16 object-contain drop-shadow-lg animate-pulse"
        />
        <div className="flex items-center gap-1.5 mt-1">
          <span className="h-1.5 w-1.5 rounded-full bg-blue-400/60 animate-bounce [animation-delay:-0.3s]" />
          <span className="h-1.5 w-1.5 rounded-full bg-blue-400/60 animate-bounce [animation-delay:-0.15s]" />
          <span className="h-1.5 w-1.5 rounded-full bg-blue-400/60 animate-bounce" />
        </div>
        <p className="text-xs text-blue-200/40 tracking-widest uppercase">
          Procredito Dominicana
        </p>
        {stuck && (
          <a
            href="/login"
            className="mt-2 text-xs text-blue-400 underline"
          >
            Tarda demasiado — ir a iniciar sesión
          </a>
        )}
      </div>
    </div>
  )
}

export function ProtectedRoute({ children }: { children?: React.ReactNode }) {
  const loading = useAuthStore(s => s.loading)
  const session = useAuthStore(s => s.session)
  const profile = useAuthStore(s => s.profile)
  const location = useLocation()
  const [waitedTooLong, setWaitedTooLong] = useState(false)

  // Seguridad: si AuthProvider no termina en 4s, dejar de bloquear
  useEffect(() => {
    if (!loading) {
      setWaitedTooLong(false)
      return
    }
    const t = window.setTimeout(() => {
      setWaitedTooLong(true)
      // Forzar fin de loading para no quedar en pantalla infinita
      useAuthStore.getState().setLoading(false)
    }, LOADING_MAX_MS)
    return () => window.clearTimeout(t)
  }, [loading])

  if (loading && !waitedTooLong) {
    return <LoadingScreen stuck={false} />
  }

  if (!session) {
    const next = encodeURIComponent(location.pathname + location.search)
    return <Navigate to={`/login?next=${next}`} replace />
  }

  if (profile?.must_change_password) return <ForcePasswordChangeScreen />

  return children ? <>{children}</> : <Outlet />
}
